/**
 * =============================================================================
 * IDS CRM Web - Authentication Context
 * =============================================================================
 *
 * PURPOSE:
 * Provides authentication state and methods throughout the application.
 * Handles login, logout, token storage, and user profile management.
 *
 * USAGE:
 * import { useAuth } from '../context/AuthContext';
 * const { user, login, logout, isAuthenticated } = useAuth();
 *
 * =============================================================================
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { forgetLists, claimLists } from '../utils/listReturn';

const AuthContext = createContext(null);

export const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

/**
 * AuthProvider component wraps the app and provides auth state.
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  /**
   * Fetches current user profile using stored token.
   */
  const fetchUser = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/v1/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const userData = await response.json();
        // Someone else signed in from another tab: they must not get the
        // lists as the last person left them.
        claimLists(userData.userId);
        setUser(userData);
      } else {
        // Token invalid or expired
        localStorage.removeItem('token');
        setToken(null);
        setUser(null);
      }
    } catch (error) {
      console.error('Failed to fetch user:', error);
      localStorage.removeItem('token');
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Fetch user on mount and token change
  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  /**
   * Logs in user with email and password.
   * @param {string} email - User email
   * @param {string} password - User password
   * @returns {Promise<Object>} User data on success
   * @throws {Error} On login failure
   */
  const login = async (email, password) => {
    const response = await fetch(`${API_URL}/api/v1/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ email, password })
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Login failed');
    }

    // Whoever signs in starts with clean lists, not the last person's filters.
    forgetLists();
    claimLists(data.user && data.user.userId);
    localStorage.setItem('token', data.token);
    setToken(data.token);
    setUser(data.user);

    return data.user;
  };

  /**
   * Logs out current user.
   */
  const logout = async () => {
    try {
      if (token) {
        await fetch(`${API_URL}/api/v1/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      forgetLists();
      localStorage.removeItem('token');
      setToken(null);
      setUser(null);
    }
  };

  /**
   * Makes an authenticated API request.
   * @param {string} endpoint - API endpoint (without base URL)
   * @param {Object} options - Fetch options
   * @returns {Promise<Response>} Fetch response
   */
  const authFetch = async (endpoint, options = {}) => {
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers
    });

    // Handle token expiry
    if (response.status === 401) {
      localStorage.removeItem('token');
      setToken(null);
      setUser(null);
    }

    return response;
  };

  // Patch the in-memory user after a self-service save (e.g. sidebar
  // customisation) so the UI reflects it without a full /auth/me round trip.
  const updateUser = (patch) => setUser(prev => (prev ? { ...prev, ...patch } : prev));

  const value = {
    user,
    token,
    loading,
    isAuthenticated: !!user,
    login,
    logout,
    authFetch,
    updateUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Hook to access auth context.
 * @returns {Object} Auth context value
 */
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
